"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import type { ExperienceSection } from "@/sanity/lib/types";
import Heading from "./Heading";
import { C, textSafe } from "./colors";

const ACCENTS = [C.violet, C.teal, C.amber, C.violet, C.rose, C.teal, C.amber];

const ExperienceTimeline = ({ section }: { section: ExperienceSection }) => {
  const experiences = section.experiences;
  const ref = useRef<HTMLElement>(null);
  const headRef = useRef<HTMLHeadingElement>(null);

  // IntersectionObserver, not GSAP's ScrollTrigger — see
  // sections/Stats.tsx for why: ScrollTrigger's setup forces a
  // synchronous layout read per trigger, which is expensive if anything
  // on the page is still settling, and these animations only ever need
  // to know once whether an element has entered the viewport.
  useEffect(() => {
    const el = ref.current;
    const head = headRef.current;
    if (!el || !head) return;
    const cards = Array.from(el.querySelectorAll<HTMLElement>(".exp-card"));
    if (!cards.length) return;
    const tweens: gsap.core.Tween[] = [];

    const headObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        tweens.push(gsap.fromTo(head, { x: -50, opacity: 0 }, { x: 0, opacity: 1, duration: 0.8, ease: "power3.out" }));
        headObserver.disconnect();
      },
      { rootMargin: "0px 0px -15% 0px" },
    );
    headObserver.observe(head);

    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const card = entry.target as HTMLElement;
          const i = cards.indexOf(card);
          tweens.push(gsap.fromTo(card, { x: i % 2 === 0 ? -60 : 60, opacity: 0 }, { x: 0, opacity: 1, duration: 0.7, ease: "power3.out" }));
          cardObserver.unobserve(card);
        });
      },
      { rootMargin: "0px 0px -12% 0px" },
    );
    cards.forEach((card) => cardObserver.observe(card));

    return () => {
      headObserver.disconnect();
      cardObserver.disconnect();
      tweens.forEach((t) => t.kill());
    };
  }, [experiences.length]);

  if (experiences.length === 0) return null;
  const h = section.sectionHeader;

  return (
    <section ref={ref} className="px-6 sm:px-16 py-24" style={{ background: C.bg }}>
      <div className="max-w-7xl mx-auto">
        {h?.eyebrow && <p className="text-sm uppercase tracking-widest mb-3" style={{ color: C.teal }}>{h.eyebrow}</p>}
        {h?.heading && h.heading.length > 0 && (
          <Heading
            ref={headRef}
            segments={h.heading}
            className="font-black mb-16"
            style={{ fontSize: "clamp(2rem,6vw,5rem)", color: C.white, fontFamily: "'Bebas Neue',sans-serif" }}
          />
        )}

        <div className="relative">
          <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px" style={{ background: `linear-gradient(${C.violet},${C.teal},${C.amber})`, opacity: 0.3 }} />
          <div className="space-y-12">
            {experiences.map((exp, i) => {
              const accent = ACCENTS[i % ACCENTS.length];
              const isRight = i % 2 === 0;
              return (
                <div key={exp._id} className={`exp-card relative flex flex-col sm:flex-row gap-6 ${isRight ? "sm:flex-row" : "sm:flex-row-reverse"}`}>
                  <div className="absolute left-4 sm:left-1/2 w-3 h-3 rounded-full -translate-x-1/2 mt-8 z-10 ring-4" style={{ background: accent }} />
                  <div className="hidden sm:block flex-1" />
                  <div className="flex-1 ml-10 sm:ml-0 rounded-2xl p-6 sm:p-8" style={{ background: C.card, border: `1px solid ${accent}25`, boxShadow: `0 0 30px ${accent}08` }}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: exp.iconBg || "#1e293b" }}>
                        {exp.icon ? (
                          <Image src={exp.icon} alt={exp.companyName} width={28} height={28} className="w-7 h-7 object-contain" />
                        ) : (
                          <span className="text-white font-bold">{exp.companyName.charAt(0)}</span>
                        )}
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-widest" style={{ color: textSafe(accent) }}>{exp.companyName}</p>
                        <p className="text-xs" style={{ color: C.dim }}>{exp.date}</p>
                      </div>
                    </div>
                    <h3 className="font-bold text-lg mb-3 leading-snug" style={{ color: C.white }}>{exp.title}</h3>
                    <ul className="space-y-1.5">
                      {exp.points.map((pt, pi) => (
                        <li key={pi} className="flex gap-2 text-sm leading-relaxed" style={{ color: C.dim }}>
                          <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: accent }} />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceTimeline;
